/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { BarSeriesData } from '../../UNSAFE_BarChart/barChart.types';
import type { LineAreaSeriesData } from '../../UNSAFE_LineAreaChart/lineAreaChart.types';
import type { Ref } from 'preact/hooks';
import type {
  ChartContextMenuContext,
  ChartContextMenuConfig,
  Group,
  Info
} from '../../utils/UNSAFE_visTypes/chart';
import { type gestureTypes, useContextMenu } from '../PRIVATE_useVisContextMenu';
import { getChartContextMenuContext } from './contextMenuUtils';

type Props<K, D> = {
  series: (BarSeriesData<K, D> | LineAreaSeriesData<K, D>)[];
  groups: Group[];
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined;
  focusedItemInfo: Info;
  rootRef: Ref<HTMLDivElement>;
  xAxisTitle?: string;
  yAxisTitle?: string;
  contextMenuConfig?: ChartContextMenuConfig<K, D>;
  onContextMenuDismissed?: (gesture: gestureTypes) => void;
};

export const useChartContextMenu = <K extends string | number, D>({
  getDataItem,
  focusedItemInfo,
  series,
  groups,
  rootRef,
  xAxisTitle,
  yAxisTitle,
  contextMenuConfig,
  onContextMenuDismissed
}: Props<K, D>) => {
  const { contextMenuProps, contextMenuContent } = useContextMenu<ChartContextMenuContext<K, D>>({
    onContextMenuHandler: ({ gesture, target }) => {
      let elem;
      const { context } = getChartContextMenuContext<K, D>(
        focusedItemInfo,
        gesture,
        target as HTMLElement,
        series,
        groups,
        rootRef,
        xAxisTitle,
        yAxisTitle,
        getDataItem
      );
      if (gesture === 'keyboard' && context.type === 'item') {
        elem = rootRef.current?.querySelector(
          `[data-oj-series-index="${context?.seriesIndex}"][data-oj-group-index="${context?.groupIndex}"]`
        );
      }
      return {
        context,
        elem
      };
    },
    contextMenuOptions: {
      isDisabled: !contextMenuConfig //If there is not a context menu renderer we disable the hook
    },
    rootRef,
    contextMenuConfig,
    onContextMenuDismissed
  });

  return { contextMenuContent, contextMenuProps };
};
